/**
 * Property Payments tab — read-only; layout matches company edit PaymentsTab (Angular banking / React parity).
 */
import { CreditCard } from 'lucide-react'
import { SectionCard } from '@/components/ui/SectionCard'
import {
  PAY_FIELD,
  PROP_INNER_CARD,
  PROP_LABEL,
  PROP_SECTION_TITLE,
} from '@/features/company/components/edit-tabs/payments/paymentsTabStyles'
import tabStyles from '@/features/company/styles/companyInfoTab.module.css'
import { cn } from '@/lib/utils/helpers'
import { format } from 'date-fns'
import { PAYMENT_TYPES, PAYMENT_VERIFY } from '../constants'
import type { PropertyPaymentsTabProps, PropertyStaffRow } from '../types'

function formatPaymentDate(val: unknown): string {
  if (val == null || val === '') return ''
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
    try {
      const d = new Date(val.replace(' ', 'T'))
      return isNaN(d.getTime()) ? String(val) : format(d, 'yyyy-MM-dd hh:mm:ss a')
    } catch {
      return String(val)
    }
  }
  return String(val)
}

/** Angular `property-edit` *ngIf="propertyData?.company_payment_setting" — show company-managed payment label. */
function isCompanyPaymentSettingActive(value: unknown): boolean {
  if (value == null || value === false) return false
  if (value === true || value === 1) return true
  const s = String(value).trim().toLowerCase()
  if (s === '' || s === '0' || s === 'false' || s === 'no') return false
  return true
}

const READ_VALUE = cn(PAY_FIELD, 'min-h-[42px]')

function resolveAttnPayee(property: Record<string, unknown>): string {
  const raw = String(property.payee_name ?? property.payeeName ?? '').trim()
  if (raw === 'LEASING' || raw === 'OTHER') return raw
  const staff = property.property_staff as PropertyStaffRow[] | undefined
  if (staff?.length && raw) {
    const u = staff.find((s) => String(s.user_id) === String(raw))
    if (u) {
      const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()
      if (name) return name
    }
  }
  return raw || '—'
}

export function PropertyPaymentsTab({ property, stateNameByCode }: PropertyPaymentsTabProps) {
  if (!property) {
    return (
      <div
        className={`${tabStyles.tabContent} text-slate-500`}
        data-testid="property-payments-loading"
      >
        Loading payment info…
      </div>
    )
  }

  const paymentMethod = String(property.payment_method ?? property.paymentMethod ?? '').trim()
  const verify = String(property.verify ?? '').trim()
  const paymentUpdatedBy = String(property.payment_updated_by ?? property.paymentUpdatedBy ?? '').trim()
  const paymentUpdatedDate = formatPaymentDate(property.payment_updated_date ?? property.paymentUpdatedDate)

  const payeeDisplay = resolveAttnPayee(property)
  const isOther = String(property.payee_name ?? property.payeeName ?? '') === 'OTHER'
  const otherName = String(property.other_payee_name ?? property.otherPayeeName ?? '')

  const paymentStateCode = String(property.payment_state ?? property.paymentState ?? '').trim()
  const paymentStateLabel = stateNameByCode?.[paymentStateCode] ?? paymentStateCode

  const methodLabel = (PAYMENT_TYPES[paymentMethod] ?? paymentMethod) || '—'
  const verifiedLabel = (PAYMENT_VERIFY[verify] ?? verify) || '—'

  const showCompanyManagedPaymentMessage = isCompanyPaymentSettingActive(
    property.company_payment_setting ?? property.companyPaymentSetting,
  )

  const sectionTitle =
    paymentMethod === 'ACH' ? 'Property payment preference' : 'Property payment info'

  return (
    <div
      className={`${tabStyles.tabContent} animate-in fade-in duration-300`}
      data-testid="property-payments-tab"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 sm:gap-4">
        <div
          data-testid="property-payments-note"
          className="min-w-0 flex-1 rounded-xl border border-amber-200/90 bg-gradient-to-br from-amber-50 to-amber-50/30 px-3 py-3 shadow-sm sm:px-3"
        >
          <p className="text-sm leading-relaxed text-amber-950/90">
            <span className="font-semibold text-amber-900">Note:</span> This information is read-only. These payment
            preferences are controlled at the company level. If you need assistance contact your Liberty Success
            Manager to set up this information.
          </p>
        </div>
      </div>

      <SectionCard
        icon={CreditCard}
        title={sectionTitle}
        testId="property-payments-preference-section"
        titleTestId="property-payments-section-1-heading"
      >
        <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
          <div className="flex flex-col gap-1.5">
            <label className={PROP_LABEL} data-testid="property-payments-method-label">
              Method
            </label>
            <div data-testid="property-payments-method" className={cn(READ_VALUE, 'w-full sm:w-40')}>
              {methodLabel}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={PROP_LABEL} data-testid="property-payments-verified-label">
              Verified
            </label>
            <div data-testid="property-payments-verified" className={cn(READ_VALUE, 'w-full sm:w-32')}>
              {verifiedLabel}
            </div>
          </div>
          <p className="pb-2 text-sm text-slate-500" data-testid="property-payments-fees-note">
            [Note: Additional processing fees may apply.]
          </p>
        </div>

        {showCompanyManagedPaymentMessage ? (
          <div className="mt-4 rounded-lg border border-slate-200/80 bg-slate-50/80 px-3 py-3" data-testid="property-payments-company-managed-message">
            <p className="text-sm font-bold text-slate-900">
              Payment settings are managed at the Company level
            </p>
          </div>
        ) : null}

        {paymentMethod === 'ACH' && (
          <div className={cn(PROP_INNER_CARD, 'mt-5')} data-testid="property-payments-ach-details">
            <p className={PROP_SECTION_TITLE}>ACH account details</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="min-w-0">
                <label className={PROP_LABEL} data-testid="property-payments-ach-routing-label">
                  ACH Routing
                </label>
                <div
                  data-testid="property-payments-ach-routing-value"
                  className={cn(READ_VALUE, 'w-full max-w-[220px] lg:max-w-none')}
                >
                  {String(property.routing_number ?? property.routingNumber ?? '—')}
                </div>
              </div>
              <div className="min-w-0">
                <label className={PROP_LABEL} data-testid="property-payments-ach-account-label">
                  ACH Account
                </label>
                <div
                  data-testid="property-payments-ach-account-value"
                  className={cn(READ_VALUE, 'w-full max-w-[220px] lg:max-w-none')}
                >
                  {String(property.account_number ?? property.accountNumber ?? '—')}
                </div>
              </div>
              <div className="min-w-0">
                <label className={PROP_LABEL} data-testid="property-payments-ach-account-type-label">
                  Account Type
                </label>
                <div
                  data-testid="property-payments-ach-account-type-value"
                  className={cn(READ_VALUE, 'w-full max-w-[220px] lg:max-w-none')}
                >
                  {String(property.property_method ?? property.propertyMethod ?? '—')}
                </div>
              </div>
              <div className="min-w-0">
                <label className={PROP_LABEL} data-testid="property-payments-ach-last-update-label">
                  Last update
                </label>
                <div
                  data-testid="property-payments-ach-last-update-value"
                  className={cn(
                    READ_VALUE,
                    'w-full max-w-[220px] whitespace-normal break-words lg:max-w-none',
                  )}
                >
                  {[paymentUpdatedBy, paymentUpdatedDate].filter(Boolean).join(' ') || '—'}
                </div>
              </div>
            </div>
          </div>
        )}

        {paymentMethod === 'PAPER_CHECK' && (
          <div className={cn(PROP_INNER_CARD, 'mt-5 space-y-4')} data-testid="property-payments-paper-details">
            <p className={PROP_SECTION_TITLE}>Paper check mailing address</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className={PROP_LABEL} data-testid="property-payments-paper-payee-label">
                  ATTN Payee
                </label>
                <div data-testid="property-payments-paper-payee-value" className={READ_VALUE}>
                  {payeeDisplay}
                </div>
                {isOther && otherName ? (
                  <div data-testid="property-payments-paper-other-payee-value" className={cn(READ_VALUE, 'mt-2')}>
                    {otherName}
                  </div>
                ) : null}
              </div>
              <div className="md:col-span-2">
                <label className={PROP_LABEL} data-testid="property-payments-paper-street-label">
                  Street Address
                </label>
                <div data-testid="property-payments-paper-street-value" className={READ_VALUE}>
                  {String(property.payment_address ?? property.paymentAddress ?? '—')}
                </div>
              </div>
              <div>
                <label className={PROP_LABEL} data-testid="property-payments-paper-suite-label">
                  Suite/Shop
                </label>
                <div data-testid="property-payments-paper-suite-value" className={READ_VALUE}>
                  {String(property.payment_shop ?? property.paymentShop ?? '—')}
                </div>
              </div>
              <div>
                <label className={PROP_LABEL} data-testid="property-payments-paper-city-label">
                  City
                </label>
                <div data-testid="property-payments-paper-city-value" className={READ_VALUE}>
                  {String(property.payment_city ?? property.paymentCity ?? '—')}
                </div>
              </div>
              <div>
                <label className={PROP_LABEL} data-testid="property-payments-paper-state-label">
                  State
                </label>
                <div data-testid="property-payments-paper-state-value" className={READ_VALUE}>
                  {paymentStateLabel || '—'}
                </div>
              </div>
              <div>
                <label className={PROP_LABEL} data-testid="property-payments-paper-zipcode-label">
                  Zipcode
                </label>
                <div data-testid="property-payments-paper-zipcode-value" className={READ_VALUE}>
                  {String(property.payment_zipcode ?? property.paymentZipcode ?? '—')}
                </div>
              </div>
            </div>
            <div
              className="flex flex-wrap items-center gap-3 text-sm text-slate-600 sm:gap-4"
              data-testid="property-payments-paper-last-update-line"
            >
              Last Update By {paymentUpdatedBy} {paymentUpdatedDate}
            </div>
          </div>
        )}

        {!paymentMethod && (
          <p className="mt-4 text-sm text-slate-600" data-testid="property-payments-no-method-message">
            No payment method on file.
          </p>
        )}
      </SectionCard>
    </div>
  )
}
